import { Link } from "react-router-dom";

interface PaginationProps {
  current: number;
  total: number;
  onChange: (page: number) => void;
}

export default function Pagination({ current, total, onChange }: PaginationProps) {
  if (total <= 1) return null;

  const pages: (number | string)[] = [];
  for (let i = 1; i <= total; i += 1) {
    if (i === 1 || i === total || Math.abs(i - current) <= 1) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== "...") {
      pages.push("...");
    }
  }

  const baseCls =
    "min-w-[40px] h-10 px-3 rounded-md text-sm font-medium flex items-center justify-center whitespace-nowrap transition-colors";

  return (
    <nav className="flex items-center justify-center flex-wrap gap-2 mt-8">
      <button
        type="button"
        disabled={current <= 1}
        onClick={() => onChange(current - 1)}
        className={`${baseCls} border border-background-300 text-foreground-700 ${
          current <= 1
            ? "opacity-40 cursor-not-allowed"
            : "hover:border-primary-400 hover:text-primary-600 cursor-pointer"
        }`}
      >
        <i className="ri-arrow-left-s-line mr-1"></i>
        上一页
      </button>

      {pages.map((p, idx) =>
        typeof p === "number" ? (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            className={`${baseCls} cursor-pointer ${
              p === current
                ? "bg-primary-500 text-background-50"
                : "border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
            }`}
          >
            {p}
          </button>
        ) : (
          <span key={`gap-${idx}`} className="px-1 text-foreground-400 select-none">
            …
          </span>
        ),
      )}

      <button
        type="button"
        disabled={current >= total}
        onClick={() => onChange(current + 1)}
        className={`${baseCls} border border-background-300 text-foreground-700 ${
          current >= total
            ? "opacity-40 cursor-not-allowed"
            : "hover:border-primary-400 hover:text-primary-600 cursor-pointer"
        }`}
      >
        下一页
        <i className="ri-arrow-right-s-line ml-1"></i>
      </button>
    </nav>
  );
}

interface BreadcrumbProps {
  items: { label: string; path?: string }[];
}

export function Breadcrumb({ items }: BreadcrumbProps) {
  return (
    <nav className="flex items-center flex-wrap gap-1 text-sm text-foreground-500">
      {items.map((item, idx) => (
        <span key={`${item.label}-${idx}`} className="flex items-center gap-1">
          {item.path ? (
            <Link
              to={item.path}
              className="hover:text-primary-600 transition-colors cursor-pointer"
            >
              {item.label}
            </Link>
          ) : (
            <span className="text-foreground-800">{item.label}</span>
          )}
          {idx < items.length - 1 && (
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
          )}
        </span>
      ))}
    </nav>
  );
}